import { api, Sprint } from '../api';
import { navigate } from '../lib/router';
import { escapeHtml, formatDate } from '../lib/utils';
import { openModal } from '../components/modal';
import { toast } from '../components/toast';
import { ProjectContext } from './project';
import { renderBoard } from './board';

export async function renderSprints(
  content: HTMLElement,
  ctx: ProjectContext,
  sprintId?: string
): Promise<void> {
  let sprints: Sprint[];
  try {
    const res = await api.get<{ sprints: Sprint[] }>(
      `/projects/${ctx.project.id}/sprints`
    );
    sprints = res.sprints;
  } catch (e: any) {
    content.innerHTML = `
      <div class="error-state">
        <h3>Could not load sprints</h3>
        <p class="muted">${escapeHtml(e.message)}</p>
      </div>
    `;
    return;
  }

  if (sprintId) {
    const sprint = sprints.find((s) => s.id === sprintId);
    if (!sprint) {
      content.innerHTML = `
        <div class="error-state">
          <h3>Sprint not found</h3>
          <p class="muted">This sprint may have been removed.</p>
          <a class="btn btn-primary" href="#${ctx.projectHref('sprints')}">Back to Sprints</a>
        </div>
      `;
      return;
    }
    await renderBoard(content, ctx, sprint);
    return;
  }

  renderSprintList(content, ctx, sprints);
}

function isActiveOrFuture(s: Sprint): boolean {
  if (s.active) return true;
  const start = new Date(s.startDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return start.getTime() >= today.getTime();
}

function renderSprintList(
  content: HTMLElement,
  ctx: ProjectContext,
  sprints: Sprint[]
): void {
  const { readOnly } = ctx;
  let activeOnly = true;

  content.innerHTML = `
    <div class="list-toolbar">
      <label class="sprint-filter">
        <input type="checkbox" id="active-sprints" checked />
        <span>Active sprints only</span>
      </label>
      ${
        readOnly
          ? '<span class="muted" style="font-size:13px;">Read-only view</span>'
          : '<button class="btn btn-primary" id="new-sprint">+ New Sprint</button>'
      }
    </div>
    <div id="sprint-list"></div>
  `;

  const listEl = content.querySelector('#sprint-list') as HTMLElement;

  const applyFilter = () => {
    const visible = sprints.filter((s) => (activeOnly ? isActiveOrFuture(s) : true));
    listEl.innerHTML = renderSprintGrid(visible, ctx, sprints);
  };

  content.querySelector('#active-sprints')!.addEventListener('change', (e) => {
    activeOnly = (e.target as HTMLInputElement).checked;
    applyFilter();
  });

  if (!readOnly) {
    content.querySelector('#new-sprint')!.addEventListener('click', () =>
      openCreateSprintModal(ctx)
    );
  }

  applyFilter();

  if (!readOnly) {
    listEl.querySelector('#new-sprint-empty')?.addEventListener('click', () =>
      openCreateSprintModal(ctx)
    );
  }
}

function renderSprintGrid(
  visible: Sprint[],
  ctx: ProjectContext,
  allSprints: Sprint[]
): string {
  if (allSprints.length === 0) {
    return `
      <div class="empty-state">
        <h3>No sprints yet</h3>
        <p class="muted">${
          ctx.readOnly
            ? 'This project has no sprints yet.'
            : 'Create a sprint to start planning work on the sprint board.'
        }</p>
        ${
          ctx.readOnly
            ? ''
            : '<button class="btn btn-primary" id="new-sprint-empty">+ New Sprint</button>'
        }
      </div>
    `;
  }

  if (visible.length === 0) {
    return `
      <div class="empty-state">
        <h3>No active sprints</h3>
        <p class="muted">Uncheck “Active sprints only” to see past sprints.</p>
      </div>
    `;
  }

  return `
    <div class="project-grid">
      ${visible
        .map((s) => {
          const total = s.taskCount;
          const done = s.completedCount;
          const percent = total === 0 ? 0 : Math.round((done / total) * 100);
          return `
          <a class="project-card" href="#${ctx.projectHref('sprints', s.id)}">
            <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:6px;">
              <h3 style="margin:0;">${escapeHtml(s.name)}</h3>
              <span class="badge" style="color:${s.active ? '#16a34a' : '#6b7280'};background:${s.active ? '#f0fdf4' : '#f3f4f6'};">
                ${s.active ? 'Active' : 'Past'}
              </span>
            </div>
            <p style="margin:0 0 12px;">Started ${formatDate(s.startDate)}</p>
            <div style="margin-bottom:12px;">
              <div class="progress-track"><div class="progress-fill" style="width:${percent}%"></div></div>
              <div class="progress-label">${percent}% complete</div>
            </div>
            <div class="project-meta">
              <span>${done}/${total} tasks done</span>
              <span style="margin-left:auto;">→</span>
            </div>
          </a>
        `;
        })
        .join('')}
    </div>
  `;
}

function openCreateSprintModal(ctx: ProjectContext): void {
  const { project } = ctx;
  const startDate = new Date();

  openModal(
    `
    <h2 style="font-size:20px;margin-bottom:16px;">New Sprint</h2>
    <form id="create-sprint-form">
      <div class="field">
        <label>Sprint name</label>
        <input class="input" name="name" placeholder="e.g. Sprint 1" />
      </div>
      <div class="field">
        <label>Start date</label>
        <input class="input" value="${formatDate(startDate.toISOString())}" disabled />
        <p class="muted" style="font-size:12px;margin:6px 0 0;">Sprints start today by default.</p>
      </div>
      <p class="muted" style="font-size:13px;margin:12px 0 0;">
        Unfinished tasks in the current sprint will be carried over to this new sprint,
        and the current sprint will be marked as past.
      </p>
      <div class="form-error hidden" id="sprint-error"></div>
      <div style="display:flex;gap:10px;justify-content:flex-end;margin-top:16px;">
        <button type="button" class="btn btn-ghost" data-close>Cancel</button>
        <button type="submit" class="btn btn-primary">Create sprint</button>
      </div>
    </form>
  `,
    (body, close) => {
      const form = body.querySelector('#create-sprint-form') as HTMLFormElement;
      const errorEl = body.querySelector('#sprint-error') as HTMLElement;
      body.querySelector('[data-close]')!.addEventListener('click', close);
      body.querySelector<HTMLInputElement>('input[name="name"]')?.focus();

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        errorEl.classList.add('hidden');
        const data = new FormData(form);
        const name = String(data.get('name') || '').trim();
        if (!name) {
          errorEl.textContent = 'Sprint name is required';
          errorEl.classList.remove('hidden');
          return;
        }
        try {
          const res = await api.post<{ sprint: Sprint; carried: number }>(
            `/projects/${project.id}/sprints`,
            { name }
          );
          toast(
            res.carried > 0
              ? `Sprint created — ${res.carried} unfinished task${res.carried === 1 ? '' : 's'} carried over`
              : 'Sprint created'
          );
          close();
          navigate(`/projects/${project.id}/sprints/${res.sprint.id}`);
        } catch (e: any) {
          errorEl.textContent = e.message || 'Could not create sprint';
          errorEl.classList.remove('hidden');
        }
      });
    }
  );
}
